import differenceBy from 'lodash/differenceBy';
import isEqual from 'lodash/isEqual';
import { getLocalesSortingFn } from '@/libraries/string';

/**
 * This method is a hacky solution for typescript cannot narrow down
 * the type in Array.prototype.filter
 * Issue: https://github.com/microsoft/TypeScript/issues/7657
 */
export function NonNull<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
}

export function removeDuplicates<T>(value: T[]): T[] {
    return [...new Set(value)];
}

/**
 * Creates an array being grouped to group of arrays which remains the same order.
 * Iteratee will receive each element and return a value indicate which group the element is.
 * @param collection
 * @param iteratee
 */
export function groupByWithOrder<T>(collection: T[], iteratee: (value: T) => any): T[][] {
    const len = collection.length;
    if (!Array.isArray(collection) || len === 0) return [];

    let prevKey = null;
    const result: T[][] = [];
    for (let i = 0; i < len; i++) {
        const element = collection[i];
        const currKey = iteratee(element);
        if (result.length === 0 || prevKey !== currKey) {
            result.push([element]);
        } else {
            result[result.length - 1].push(element);
        }
        prevKey = currKey;
    }
    return result;
}

/**
 * Assigns destination array items to be same as source array. Order is not guaranteed. This method mutates array.
 * @param array destination array to modify
 * @param source source of items to updates/deletes
 * @param keySelector function to get item's key to compare for equivalency
 */
export function assignArray<T, K>(array: T[], source: T[], keySelector: (item: T) => K) {
    const toDelete = differenceBy(array, source, keySelector);
    remove(array, toDelete, (a, b) => keySelector(a) === keySelector(b));
    upsert(array, source, keySelector);
}

export function upsert<T, K>(list: T[], toUpsert: T[], keySelector: (item: T, index: number, array: T[]) => K) {
    const map = createMapping(list, keySelector, (item, index) => ({ item, index }));

    toUpsert.forEach((item, index, array) => {
        const originalItem = map.get(keySelector(item, index, array));
        if (!originalItem) {
            list.push(item);
        } else if (!isEqual(originalItem, item)) {
            list.splice(originalItem.index, 1, item);
        }
    });
}

export function remove<T, R>(list: T[], toDelete: R[], comparator: (a: T, b: R) => boolean) {
    toDelete.forEach((item) => {
        const index = list.findIndex(i => comparator(i, item));
        if (index !== -1) {
            list.splice(index, 1);
        }
    });
}

export function createMapping<T, K, V>(list: T[], keySelector: (item: T, index: number, array: T[]) => K, valueSelector: (item: T, index: number, array: T[]) => V) {
    return new Map(list.map((item, index, array) => [
        keySelector(item, index, array),
        valueSelector(item, index, array),
    ]));
}

export function moveElement<T>(array: Array<T>, from: number, to: number) {
    const elementToMove = array.splice(from, 1)[0];
    array.splice(to, 0, elementToMove);
}

export const getLastElement = <T>(arr: T[]) => arr[arr.length - 1];

export const padRight = <T>(arr: T[], len: number) => [...arr, ...(new Array(len).fill(null))].slice(0, Math.max(arr.length, len));

export function _2dArrayPadRight<T>(arr: T[], expectedLength: number, isWrapped: boolean) {
    const transformedArray = arr.reduce<T[][]>((acc, curr) => {
        if (getLastElement(acc).length === expectedLength && isWrapped) {
            acc.push([]);
        }
        getLastElement(acc).push(curr);
        return acc;
    }, [[]]);

    transformedArray[transformedArray.length - 1] = padRight(getLastElement(transformedArray), expectedLength);
    return transformedArray;
}

export function sortByLocales<T>(list: T[], fn: (item: T) => string) {
    return list.sort((a, b) => {
        const sortFn = getLocalesSortingFn();
        return sortFn(fn(a), fn(b));
    });
}
